import { appError } from '@pkg/shared/errors'
import { logger } from '@pkg/shared/logger'
import { APP_METADATA_NODE_ID_KEY } from '@pkg/sync/common'
import { generateNodeId } from '@pkg/sync/common/generateNodeId'
import { environmentManager } from '@tanstack/react-query'
import type { SQLocal } from 'sqlocal'

export type DatabaseInitializerHooks = {
  /**
   * Progress in percent (0..100). If total size can't be determined, progress may be `null`.
   */
  onProgress?: (progress: number | null) => void
  onInitStarted?: () => void
  onInitFinished?: (isNew: boolean) => void
  onError?: (error: Error) => void
  onWaitingForSnapshot?: (info: { waitMs: number; elapsedMs: number }) => void
}

export interface DatabaseInitializer {
  /**
   * Idempotent.
   * - The first call performs initialization.
   * - Concurrent calls await the same underlying work.
   */
  initialize: (hooks?: DatabaseInitializerHooks) => Promise<void>
  waitForInit: Promise<void>
}

export interface DatabaseInitializerConfig {
  sqlocal?: SQLocal
  /**
   * Resolves to a presigned URL for the prebuilt snapshot. The consumer is
   * responsible for polling its source (e.g. an oRPC procedure throwing
   * SERVICE_UNAVAILABLE) until a URL is available.
   */
  fetchSnapshot?: () => Promise<{ url: string; uncompressedSize?: number }>
  onInit?: (isNew: boolean) => void | Promise<void>
}

export const createDatabaseInitializer = (config: DatabaseInitializerConfig): DatabaseInitializer => {
  const { sqlocal, fetchSnapshot, onInit } = config
  if (!sqlocal) {
    if (environmentManager.isServer()) {
      return {
        initialize: async () => {},
        waitForInit: Promise.resolve(),
      }
    } else {
      throw appError('INTERNAL', 'Database initializer requires SQLocal instance in client environment')
    }
  }

  let resolveOnInit: () => void
  let rejectOnInit: (error: Error) => void
  const waitForInit = new Promise<void>((resolve, reject) => {
    resolveOnInit = resolve
    rejectOnInit = reject
  })

  let isInitializing = false
  const initialize = async (hooks: DatabaseInitializerHooks = {}) => {
    if (isInitializing) {
      return waitForInit
    }
    isInitializing = true
    logger.info('Initializing database...')
    hooks.onInitStarted?.()
    let isNew = false

    try {
      const info = await sqlocal.getDatabaseInfo()

      const isInitialized = (info?.databaseSizeBytes ?? 0) > 0

      // const isDbEmpty = (info.databaseSizeBytes ?? 0) <= 10
      logger.info(`Has initialized database: ${isInitialized}`)

      if (!isInitialized && fetchSnapshot) {
        const { url, uncompressedSize } = await fetchSnapshot()
        logger.info({ url }, 'Fetching prebuilt database from presigned URL')
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Failed to fetch prebuilt database: ${response.statusText}`)
        }

        const contentLength = response.headers.get('content-length')
        const contentEncoding = response.headers.get('content-encoding')
        const isIdentityEncoding = !contentEncoding || contentEncoding.toLowerCase() === 'identity'

        // In browsers, fetch() transparently decompresses encoded responses, but
        // `content-length` reflects the compressed payload size. For an accurate
        // progress percentage, prefer `uncompressedSize` from the callback when the
        // response is compressed.
        const totalFromCallback = !isIdentityEncoding && uncompressedSize ? uncompressedSize : undefined
        const headerLength = contentLength ? parseInt(contentLength, 10) : 0
        const total = totalFromCallback ?? (isIdentityEncoding ? headerLength : 0)
        const hasUsableTotal = total > 0

        logger.info(`Received response for prebuilt database, total size: ${total} bytes. Fetching`)

        let blob: Blob
        if (response.body) {
          const reader = response.body.getReader()
          let loaded = 0
          const chunks: Uint8Array[] = []
          let lastProgress = 0

          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            if (value) {
              chunks.push(value)
              loaded += value.length
              if (hasUsableTotal) {
                const progress = Math.min(100, (loaded / total) * 100)
                // Keep progress monotonic to avoid jitter due to rounding/chunking.
                lastProgress = Math.max(lastProgress, progress)
                hooks.onProgress?.(lastProgress)
              }
            }
          }

          if (chunks.length === 0) {
            throw new Error('No data received for prebuilt database')
          }

          blob = new Blob(chunks as never)
          isNew = true

          // If we didn't report intermediate progress (e.g. compressed/unknown total), still complete the UI.
          hooks.onProgress?.(100)
        } else {
          // Fallback if response.body is not available
          blob = await response.blob()
          if (blob.size === 0) {
            throw new Error('No data received for prebuilt database')
          }
          isNew = true

          // Best-effort: we can't reliably track progress without streaming.
          hooks.onProgress?.(100)
        }
        await sqlocal.overwriteDatabaseFile(blob, async () => {
          logger.info('Database file overwritten successfully')
          await sqlocal.sql(
            `insert into appMetadata (key, value) values ('${APP_METADATA_NODE_ID_KEY}', '${generateNodeId()}')`,
          )
        })
      }

      logger.info('Database setup complete. Running onInit')
      await onInit?.(isNew)

      hooks.onInitFinished?.(isNew)
      logger.info('Database initialization complete')
    } catch (err) {
      logger.error({ err }, 'Database initialization failed')
      const error = appError('INTERNAL', 'Database could not be build', { cause: err })
      hooks.onError?.(error)
      rejectOnInit(error)
      throw error
    }
    resolveOnInit()
  }

  return {
    initialize,
    waitForInit,
  }
}
